import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setTokens, clearTokens, getRefreshToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setLang } = useI18n();

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get("/users/me");
      setUser(data);
      if (data?.settings?.language) setLang(data.settings.language);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, [setLang]);

  useEffect(() => {
    (async () => {
      if (localStorage.getItem("urlifa_access")) {
        await loadUser();
      }
      setLoading(false);
    })();
  }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens(data.access_token, data.refresh_token);
    return await loadUser();
  };

  const register = async (email, password, full_name) => {
    const { data } = await api.post("/auth/register", { email, password, full_name });
    setTokens(data.access_token, data.refresh_token);
    return await loadUser();
  };

  const logout = async () => {
    try {
      const rt = getRefreshToken();
      if (rt) await api.post("/auth/logout", { refresh_token: rt });
    } catch (e) { /* ignore */ }
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refreshUser: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
